import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail, formatEuro } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import AppHeader from "@/components/AppHeader";
import { Plus, Search, Trash2, Pencil, Check, Minus, ScanLine, FileUp, Loader2 } from "lucide-react";

function emptyItems() {
  return {
    kinder_res: 0, kinder_no_res: 0,
    erwachsene_res: 0, erwachsene_no_res: 0,
    sav_res: 0, sav_no_res: 0,
    gutschein: 0,
  };
}

function CategoryStepper({ label, price, value, onChange, testid }) {
  return (
    <div className="border border-zinc-200 rounded-lg p-3 flex flex-col gap-2">
      <div>
        <p className="text-sm font-semibold leading-tight">{label}</p>
        <p className="text-xs text-zinc-500">{formatEuro(price)}</p>
      </div>
      <div className="flex items-center justify-between gap-2">
        <button type="button" onClick={() => onChange(Math.max(0, value - 1))}
          data-testid={`${testid}-minus`}
          className="h-11 w-11 rounded-md border border-zinc-200 hover:bg-zinc-50 flex items-center justify-center">
          <Minus className="h-4 w-4" />
        </button>
        <span data-testid={`${testid}-value`} className="font-mono text-2xl font-bold tabular-nums w-10 text-center">
          {value}
        </span>
        <button type="button" onClick={() => onChange(value + 1)}
          data-testid={`${testid}-plus`}
          className="h-11 w-11 rounded-md bg-zinc-950 hover:bg-zinc-800 text-white flex items-center justify-center">
          <Plus className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

function ReservationModal({ open, onClose, onSaved, initial }) {
  const { config } = useAuth();
  const isEdit = !!initial;
  const [name, setName] = useState(initial?.name || "");
  const [phone, setPhone] = useState(initial?.phone || "");
  const [notes, setNotes] = useState(initial?.notes || "");
  const [items, setItems] = useState(initial?.items || emptyItems());
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const { showId } = useParams();

  const total = useMemo(() => {
    if (!config) return 0;
    return Object.keys(items).reduce(
      (sum, k) => sum + (config.prices[k] || 0) * (items[k] || 0),
      0
    );
  }, [items, config]);

  if (!open || !config) return null;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErr("");
    try {
      const payload = { name, phone, notes, items };
      if (isEdit) await api.put(`/reservations/${initial.id}`, payload);
      else await api.post(`/shows/${showId}/reservations`, payload);
      onSaved();
      onClose();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-zinc-950/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <form onSubmit={submit} className="p-8 space-y-5">
          <h2 className="text-2xl font-black tracking-tight">
            {isEdit ? "Reservierung bearbeiten" : "Neue Reservierung"}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-500 mb-2 block">Name</label>
              <input data-testid="res-form-name" value={name} onChange={(e) => setName(e.target.value)} required autoFocus
                className="w-full h-12 px-3 text-base border border-zinc-300 rounded-md focus:outline-none focus:ring-2 focus:ring-zinc-950" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-500 mb-2 block">Telefon</label>
              <input data-testid="res-form-phone" value={phone} onChange={(e) => setPhone(e.target.value)}
                className="w-full h-12 px-3 text-base border border-zinc-300 rounded-md focus:outline-none focus:ring-2 focus:ring-zinc-950" />
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-500 mb-2 block">Notiz</label>
            <input data-testid="res-form-notes" value={notes} onChange={(e) => setNotes(e.target.value)}
              className="w-full h-12 px-3 text-base border border-zinc-300 rounded-md focus:outline-none focus:ring-2 focus:ring-zinc-950" />
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-500 mb-3">Karten</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {config.order.filter((k) => k !== "gutschein").map((k) => (
                <CategoryStepper
                  key={k}
                  testid={`res-form-cat-${k}`}
                  label={config.labels[k]}
                  price={config.prices[k]}
                  value={items[k] || 0}
                  onChange={(v) => setItems({ ...items, [k]: v })}
                />
              ))}
            </div>
          </div>
          <div className="flex items-center justify-between border-t pt-4">
            <span className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-500">Reservierungswert</span>
            <span data-testid="res-form-total" className="font-mono text-3xl font-black tabular-nums">{formatEuro(total)}</span>
          </div>
          {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-4 py-3">{err}</div>}
          <div className="flex gap-3 justify-end pt-2">
            <button type="button" onClick={onClose}
              className="h-12 px-5 rounded-md border border-zinc-200 hover:bg-zinc-50 font-semibold">Abbrechen</button>
            <button data-testid="res-form-save" type="submit" disabled={saving}
              className="h-12 px-6 rounded-md bg-zinc-950 hover:bg-zinc-800 text-white font-bold disabled:opacity-60">
              {saving ? "Speichern…" : "Speichern"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function PdfImportModal({ open, onClose, onDone, showId }) {
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [rows, setRows] = useState(null); // null = not parsed yet
  const [replaceExisting, setReplaceExisting] = useState(true);
  const [err, setErr] = useState("");
  const [importing, setImporting] = useState(false);

  if (!open) return null;

  const preview = async () => {
    if (!file) return;
    setParsing(true);
    setErr("");
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post(`/shows/${showId}/import-reservations/preview`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setRows(data.rows);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
    setParsing(false);
  };

  const confirm = async () => {
    if (!rows || rows.length === 0) return;
    setImporting(true);
    setErr("");
    try {
      const { data } = await api.post(`/shows/${showId}/import-reservations/confirm`, {
        rows,
        replace_existing: replaceExisting,
      });
      onDone(data);
      onClose();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
    setImporting(false);
  };

  const updateRow = (idx, patch) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };
  const removeRow = (idx) => setRows((prev) => prev.filter((_, i) => i !== idx));

  return (
    <div className="fixed inset-0 z-50 bg-zinc-950/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-8 space-y-5">
          <div>
            <h2 className="text-2xl font-black tracking-tight">Gästeliste als PDF importieren</h2>
            <p className="text-zinc-500 mt-1 text-sm">
              Lade die PDF-Gästeliste hoch. Das System liest Name + Platzanzahl + Sitze automatisch aus.
              Standardmäßig werden alle Karten als &bdquo;Erwachsene mit Reservierung&ldquo; eingetragen — du kannst das pro Reservierung später anpassen.
            </p>
          </div>

          {!rows ? (
            <>
              <label className="block">
                <span className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-500 mb-2 block">PDF-Datei</span>
                <input
                  data-testid="pdf-import-file"
                  type="file"
                  accept="application/pdf"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  className="block w-full text-sm text-zinc-700 file:mr-4 file:h-12 file:px-5 file:rounded-md file:border-0 file:bg-zinc-950 file:text-white file:font-bold file:hover:bg-zinc-800 file:cursor-pointer cursor-pointer border border-dashed border-zinc-300 rounded-md p-3"
                />
              </label>
              {file && <p className="text-sm text-zinc-600">{file.name} · {(file.size / 1024).toFixed(0)} KB</p>}
              {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-4 py-3">{err}</div>}
              <div className="flex gap-3 justify-end pt-2">
                <button type="button" onClick={onClose}
                  className="h-12 px-5 rounded-md border border-zinc-200 hover:bg-zinc-50 font-semibold">Abbrechen</button>
                <button data-testid="pdf-import-parse" disabled={!file || parsing} onClick={preview}
                  className="h-12 px-6 rounded-md bg-zinc-950 hover:bg-zinc-800 text-white font-bold disabled:opacity-60 flex items-center gap-2">
                  {parsing && <Loader2 className="h-4 w-4 animate-spin" />}
                  PDF einlesen
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-900">
                <strong>Wichtig:</strong> Die Kategorie (Kinder / Erwachsene / SAV) wird erst an der Kassa pro Reservierung festgelegt — hier importierst du nur Name, Karten-Anzahl und Sitzplätze.
              </div>

              <label className="flex items-start gap-3 cursor-pointer bg-zinc-50 border border-zinc-200 rounded-lg p-4">
                <input
                  type="checkbox"
                  data-testid="pdf-import-replace"
                  checked={replaceExisting}
                  onChange={(e) => setReplaceExisting(e.target.checked)}
                  className="mt-1 h-5 w-5 accent-zinc-950"
                />
                <span className="text-sm">
                  <strong>Alte Reservierungen ersetzen</strong><br/>
                  <span className="text-zinc-500">Alle bisherigen, noch nicht kassierten Reservierungen werden vorher gelöscht. Bereits kassierte Reservierungen bleiben erhalten.</span>
                </span>
              </label>

              {/* Plausibilitäts-Check: Σ Anzahl vs Σ Sitze pro Reservierung */}
              {(() => {
                const totalPlatz = rows.reduce((s, r) => s + (Number(r.platzanzahl) || 0), 0);
                const totalSeats = rows.reduce((s, r) => s + (r.seats || "").split(",").map((x) => x.trim()).filter(Boolean).length, 0);
                const mismatches = rows
                  .map((r, idx) => ({ idx, name: r.name, want: Number(r.platzanzahl) || 0,
                                      have: (r.seats || "").split(",").map((x) => x.trim()).filter(Boolean).length }))
                  .filter((m) => m.want !== m.have);
                if (totalPlatz === totalSeats && mismatches.length === 0) {
                  return (
                    <div data-testid="pdf-import-summary-ok" className="bg-emerald-50 border-2 border-emerald-300 rounded-lg p-3 text-emerald-900 text-sm font-bold flex items-center gap-2">
                      ✓ Plausibilitäts-Check OK: {rows.length} Reservierungen · {totalPlatz} Plätze · {totalSeats} Sitze stimmen überein.
                    </div>
                  );
                }
                return (
                  <div data-testid="pdf-import-summary-warn" className="bg-amber-50 border-2 border-amber-300 rounded-lg p-3 text-amber-900 text-sm">
                    <p className="font-black">⚠ Warnung — bitte vor Import prüfen</p>
                    <p className="mt-1">{rows.length} Reservierungen erwartet <strong>{totalPlatz}</strong> Plätze, im PDF gefunden <strong>{totalSeats}</strong> Sitze.</p>
                    {mismatches.length > 0 && (
                      <ul className="mt-2 list-disc list-inside text-xs">
                        {mismatches.slice(0, 6).map((m) => (
                          <li key={m.idx}>Zeile {m.idx + 1} (&bdquo;{m.name || "ohne Namen"}&ldquo;): Anzahl <strong>{m.want}</strong>, gefundene Sitze <strong>{m.have}</strong></li>
                        ))}
                        {mismatches.length > 6 && <li>… und {mismatches.length - 6} weitere</li>}
                      </ul>
                    )}
                  </div>
                );
              })()}

              <div className="border border-zinc-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-zinc-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs uppercase tracking-widest text-zinc-500">Res.Nr</th>
                      <th className="px-3 py-2 text-left text-xs uppercase tracking-widest text-zinc-500">Name</th>
                      <th className="px-3 py-2 text-right text-xs uppercase tracking-widest text-zinc-500">Karten</th>
                      <th className="px-3 py-2 text-left text-xs uppercase tracking-widest text-zinc-500">Sitze</th>
                      <th className="px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => {
                      const want = Number(r.platzanzahl) || 0;
                      const have = (r.seats || "").split(",").map((x) => x.trim()).filter(Boolean).length;
                      const mismatch = want !== have;
                      return (
                      <tr key={i} data-testid={`pdf-import-row-${i}`} className={`border-b border-zinc-100 ${mismatch ? "bg-amber-50" : ""}`}>
                        <td className="px-3 py-2 font-mono text-xs text-zinc-500">{r.res_nr || "—"}</td>
                        <td className="px-3 py-2">
                          <input
                            data-testid={`pdf-import-row-name-${i}`}
                            value={r.name}
                            onChange={(e) => updateRow(i, { name: e.target.value })}
                            className="w-full h-9 px-2 border border-zinc-200 rounded focus:outline-none focus:ring-1 focus:ring-zinc-950"
                          />
                        </td>
                        <td className="px-3 py-2 text-right">
                          <input
                            data-testid={`pdf-import-row-platz-${i}`}
                            type="number" min="0"
                            value={r.platzanzahl}
                            onChange={(e) => updateRow(i, { platzanzahl: Number(e.target.value) || 0 })}
                            className={`w-16 h-9 px-2 border rounded text-right focus:outline-none focus:ring-1 focus:ring-zinc-950 ${mismatch ? "border-amber-400 bg-amber-100 font-bold" : "border-zinc-200"}`}
                          />
                          {mismatch && <span className="block text-[10px] text-amber-700 mt-0.5">≠ {have} Sitze</span>}
                        </td>
                        <td className="px-3 py-2 text-xs text-zinc-500 max-w-[200px] truncate" title={r.seats}>{r.seats || "—"}</td>
                        <td className="px-3 py-2 text-right">
                          <button data-testid={`pdf-import-row-remove-${i}`} onClick={() => removeRow(i)}
                            className="h-8 w-8 rounded border border-red-200 text-red-700 hover:bg-red-50 inline-flex items-center justify-center">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-4 py-3">{err}</div>}

              <div className="flex gap-3 justify-end pt-2">
                <button type="button" onClick={() => setRows(null)}
                  className="h-12 px-5 rounded-md border border-zinc-200 hover:bg-zinc-50 font-semibold">Zurück</button>
                <button data-testid="pdf-import-confirm" onClick={confirm} disabled={importing || rows.length === 0}
                  className="h-12 px-6 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white font-bold disabled:opacity-60 flex items-center gap-2">
                  {importing && <Loader2 className="h-4 w-4 animate-spin" />}
                  <Check className="h-4 w-4" />
                  {rows.length} Reservierung{rows.length !== 1 ? "en" : ""} importieren
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ReservationsPage() {
  const { showId } = useParams();
  const { config } = useAuth();
  const navigate = useNavigate();
  const [show, setShow] = useState(null);
  const [reservations, setReservations] = useState(null);
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState({ open: false, initial: null });
  const [importOpen, setImportOpen] = useState(false);
  const [importNotice, setImportNotice] = useState(null);

  const load = async () => {
    try {
      const [s, r] = await Promise.all([
        api.get(`/shows/${showId}`),
        api.get(`/shows/${showId}/reservations`),
      ]);
      setShow(s.data);
      setReservations(r.data);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [s, r] = await Promise.all([
          api.get(`/shows/${showId}`),
          api.get(`/shows/${showId}/reservations`),
        ]);
        if (cancelled) return;
        setShow(s.data);
        setReservations(r.data);
      } catch (e) {
        console.error(e);
      }
    })();
    return () => { cancelled = true; };
  }, [showId]);

  const loading = !show || reservations === null;

  const remove = async (id) => {
    if (!window.confirm("Reservierung löschen?")) return;
    await api.delete(`/reservations/${id}`);
    load();
  };

  const filtered = (reservations || []).filter((r) =>
    r.name.toLowerCase().includes(search.toLowerCase()) ||
    (r.phone || "").includes(search)
  );

  const computeTotal = (items) => {
    if (!config) return 0;
    return Object.keys(items).reduce(
      (sum, k) => sum + (config.prices[k] || 0) * (items[k] || 0),
      0
    );
  };

  const ticketsLabel = (items) => {
    if (!config) return "";
    const parts = [];
    config.order.forEach((k) => {
      const v = items[k] || 0;
      if (v > 0) parts.push(`${v}× ${config.labels[k].split(" (")[0]}`);
    });
    return parts.join(" · ") || "—";
  };

  return (
    <div className="min-h-screen bg-zinc-50">
      <AppHeader
        title={show ? `Reservierungen · ${show.title}` : "Reservierungen"}
        right={
          <>
            <button
              data-testid="pdf-import-btn"
              onClick={() => setImportOpen(true)}
              className="h-11 px-4 rounded-md border border-zinc-200 hover:bg-zinc-50 font-bold flex items-center gap-2 text-sm"
            >
              <FileUp className="h-4 w-4" /> PDF importieren
            </button>
            <button
              data-testid="open-kassa-btn"
              onClick={() => navigate(`/shows/${showId}/kassa`)}
              className="h-11 px-4 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white font-bold flex items-center gap-2 text-sm"
            >
              <ScanLine className="h-4 w-4" /> Zur Kassa
            </button>
            <button
              data-testid="new-reservation-btn"
              onClick={() => setModal({ open: true, initial: null })}
              className="h-11 px-4 rounded-md bg-zinc-950 hover:bg-zinc-800 text-white font-bold flex items-center gap-2 text-sm"
            >
              <Plus className="h-4 w-4" /> Reservierung
            </button>
          </>
        }
      />
      <main className="max-w-7xl mx-auto px-6 py-8 space-y-6">
        {importNotice && (
          <div data-testid="import-notice" className="bg-emerald-50 border-2 border-emerald-300 text-emerald-900 rounded-xl px-5 py-4 text-sm">
            <p className="font-black text-base">
              ✓ <strong>{importNotice.created}</strong> Reservierung{importNotice.created !== 1 ? "en" : ""} importiert
              {importNotice.deleted > 0 ? ` · ${importNotice.deleted} alte gelöscht` : ""}
              {importNotice.skipped > 0 ? ` · ${importNotice.skipped} übersprungen` : ""}.
            </p>
            {typeof importNotice.totalSeats === "number" && (
              <p className="mt-1">
                Heute sind <strong>{importNotice.totalSeats}</strong> Plätze reserviert ({importNotice.totalRows} Reservierungen).
              </p>
            )}
          </div>
        )}
        <div className="bg-white border border-zinc-200 rounded-xl p-4 flex items-center gap-3">
          <Search className="h-5 w-5 text-zinc-400 ml-2" />
          <input
            data-testid="reservations-search"
            placeholder="Suche nach Name oder Telefon…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1 h-11 text-base border-none focus:outline-none bg-transparent"
          />
          <span className="text-sm text-zinc-500 mr-2">{filtered.length} Einträge</span>
        </div>

        {/* Gebuchte Plätze gesamt */}
        {reservations && reservations.length > 0 && (() => {
          const totalSeatsAll = reservations.reduce((s, r) => s + (Number(r.total_seats) || 0), 0);
          const totalActiveSeats = reservations.reduce((s, r) => {
            const cancelled = (r.cancelled_seats || []).length;
            return s + Math.max(0, (Number(r.total_seats) || 0) - cancelled);
          }, 0);
          const paidSeats = reservations.reduce((s, r) => s + ((r.paid_seats || []).length), 0);
          return (
            <div data-testid="reservations-summary" className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white border-2 border-zinc-200 rounded-xl p-4">
                <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-zinc-500">Reservierungen</p>
                <p className="font-mono text-3xl font-black tabular-nums mt-1">{reservations.length}</p>
              </div>
              <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4">
                <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-blue-900">Reservierte Plätze</p>
                <p className="font-mono text-3xl font-black tabular-nums mt-1 text-blue-900">{totalActiveSeats}</p>
                <p className="text-[10px] text-blue-700 mt-1">aktiv (ohne Stornos)</p>
              </div>
              <div className="bg-emerald-50 border-2 border-emerald-200 rounded-xl p-4">
                <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-emerald-900">Bereits bezahlt</p>
                <p className="font-mono text-3xl font-black tabular-nums mt-1 text-emerald-900">{paidSeats}</p>
              </div>
              <div className="bg-zinc-50 border-2 border-zinc-200 rounded-xl p-4">
                <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-zinc-500">Freie Sitze im Saal</p>
                <p className="font-mono text-3xl font-black tabular-nums mt-1">{Math.max(0, 216 - totalActiveSeats)}</p>
                <p className="text-[10px] text-zinc-500 mt-1">von 216 (18×12)</p>
              </div>
            </div>
          );
        })()}

        {loading ? (
          <p className="text-zinc-500">Lade…</p>
        ) : filtered.length === 0 ? (
          <div className="bg-white border border-dashed border-zinc-300 rounded-xl p-12 text-center">
            <p className="text-xl font-bold mb-2">Keine Reservierungen</p>
            <p className="text-zinc-500">Lege die erste Reservierung an oder warte auf Anrufe.</p>
          </div>
        ) : (
          <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
            <table className="w-full text-left">
              <thead className="bg-zinc-50 border-b border-zinc-200">
                <tr>
                  <th className="px-5 py-3 text-xs uppercase tracking-widest text-zinc-500">Name</th>
                  <th className="px-5 py-3 text-xs uppercase tracking-widest text-zinc-500">Karten / Kategorie</th>
                  <th className="px-5 py-3 text-xs uppercase tracking-widest text-zinc-500">Sitzplätze</th>
                  <th className="px-5 py-3 text-xs uppercase tracking-widest text-zinc-500">Status</th>
                  <th className="px-5 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => {
                  const itemsSum = Object.values(r.items || {}).reduce((a, b) => a + b, 0);
                  const totalSeats = r.total_seats || itemsSum;
                  const hasCategories = itemsSum > 0;
                  return (
                  <tr key={r.id} data-testid={`reservation-row-${r.id}`} className="border-b border-zinc-100 hover:bg-zinc-50">
                    <td className="px-5 py-4">
                      <p className="font-semibold text-base">{r.name}</p>
                      <p className="text-xs text-zinc-500">{r.phone || "—"}{r.notes ? ` · ${r.notes}` : ""}</p>
                    </td>
                    <td className="px-5 py-4 text-sm text-zinc-700">
                      {hasCategories ? (
                        <>
                          <p>{ticketsLabel(r.items)}</p>
                          <p className="text-xs text-zinc-500 mt-1 font-mono">{formatEuro(computeTotal(r.items))}</p>
                        </>
                      ) : (
                        <>
                          <p className="font-mono font-bold">{totalSeats} Karten reserviert</p>
                          <p className="text-xs text-zinc-500 mt-1">Kategorie wird an der Kassa festgelegt</p>
                        </>
                      )}
                    </td>
                    <td className="px-5 py-4 text-xs text-zinc-600 font-mono max-w-[260px] truncate" title={r.seats || ""}>
                      {r.seats || "—"}
                    </td>
                    <td className="px-5 py-4">
                      {r.status === "paid" ? (
                        <span className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-widest bg-emerald-50 text-emerald-700 border border-emerald-200 px-2.5 py-1 rounded">
                          <Check className="h-3 w-3" /> Bezahlt
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-widest bg-amber-50 text-amber-800 border border-amber-200 px-2.5 py-1 rounded">
                          Offen
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex gap-2 justify-end">
                        <button data-testid={`res-edit-${r.id}`} onClick={() => setModal({ open: true, initial: r })}
                          className="h-10 w-10 rounded-md border border-zinc-200 hover:bg-zinc-50 flex items-center justify-center">
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button data-testid={`res-delete-${r.id}`} onClick={() => remove(r.id)}
                          className="h-10 w-10 rounded-md border border-red-200 text-red-700 hover:bg-red-50 flex items-center justify-center">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </main>
      <ReservationModal key={modal.open ? (modal.initial?.id || 'new') : 'closed-res'} open={modal.open} initial={modal.initial} onClose={() => setModal({ open: false, initial: null })} onSaved={load} />
      <PdfImportModal
        key={importOpen ? 'import-open' : 'import-closed'}
        open={importOpen}
        showId={showId}
        onClose={() => setImportOpen(false)}
        onDone={async (res) => {
          await load();
          // Hole frische Reservierungs-Liste, um die Gesamt-Plätze für die Notice anzuzeigen
          try {
            const fresh = await api.get(`/shows/${showId}/reservations`);
            const totalSeats = fresh.data.reduce((s, r) => s + (Number(r.total_seats) || 0), 0);
            setImportNotice({ ...res, totalSeats, totalRows: fresh.data.length });
          } catch {
            setImportNotice(res);
          }
          setTimeout(() => setImportNotice(null), 8000);
        }}
      />
    </div>
  );
}
